import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  max,
  min,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { DateRange, DefinedRange, WeekStartsOn } from '@topconagriculture/react-components';
import { DateLike } from '@topconagriculture/language';

import { parseDate, toLocalDate, toTimezoneDate } from '../../core/helpers/utils/dateUtils';
import { DateRangeType, PresetDateRanges } from '../../config/constants';

export const chunks = <T>(array: ReadonlyArray<T>, size: number): T[][] => {
  return Array.from({ length: Math.ceil(array.length / size) }, (v, i) => array.slice(i * size, i * size + size));
};

export const getDaysInMonth = (date: Date, weekStartsOn?: WeekStartsOn) => {
  const startWeek = startOfWeek(startOfMonth(date), { weekStartsOn });
  const endWeek = endOfWeek(endOfMonth(date), { weekStartsOn });
  const days = [];
  for (let curr = startWeek; isBefore(curr, endWeek); ) {
    days.push(curr as never);
    curr = addDays(curr, 1);
  }
  return days;
};

export const isStartOfRange = ({ startDate }: DateRange, day: Date) =>
  (startDate && isSameDay(day, startDate)) as boolean;

export const isEndOfRange = ({ endDate }: DateRange, day: Date) => (endDate && isSameDay(day, endDate)) as boolean;

export const inDateRange = ({ startDate, endDate }: DateRange, day: Date) =>
  (startDate &&
    endDate &&
    (isWithinInterval(day, { start: startDate, end: endDate }) ||
      isSameDay(day, startDate) ||
      isSameDay(day, endDate))) as boolean;

export const isRangeSameDay = ({ startDate, endDate }: DateRange) => {
  if (startDate && endDate) {
    return isSameDay(startDate, endDate);
  }
  return false;
};

export const parseOptionalDate = (date: DateLike, defaultValue: Date) => {
  const parsed = parseDate(date);
  return parsed ?? defaultValue;
};

export const generateYears = (relativeTo: Date, count: number) => {
  const half = Math.floor(count / 2);
  return Array(count)
    .fill(0)
    .map((y, i) => relativeTo.getFullYear() - half + i);
};

export const todayDateRange = (date: Date): DefinedRange => ({
  labelKey: 'today',
  startDate: startOfDay(date),
  endDate: endOfDay(date),
});

export const yesterdayDateRange = (date: Date): DefinedRange => ({
  labelKey: 'yesterday',
  startDate: startOfDay(addDays(date, -1)),
  endDate: endOfDay(addDays(date, -1)),
});

export const last7DaysDateRange = (date: Date): DefinedRange => ({
  labelKey: 'last_7_days',
  startDate: startOfDay(addDays(date, -6)),
  endDate: endOfDay(date),
});

export const lastMonthDateRange = (date: Date): DefinedRange => ({
  labelKey: 'last_month',
  startDate: startOfDay(startOfMonth(addMonths(date, -1))),
  endDate: endOfDay(endOfMonth(addMonths(date, -1))),
});

export const getDefaultRanges = (date: Date): DefinedRange[] => [
  todayDateRange(date),
  yesterdayDateRange(date),
  last7DaysDateRange(date),
  lastMonthDateRange(date),
];

export const getValidatedMonths = (range: DateRange, minimumDate: Date, maximumDate: Date) => {
  const { startDate, endDate } = range;
  if (startDate && endDate) {
    const newStart = max([startDate, minimumDate]);
    const newEnd = min([endDate, maximumDate]);
    return [newStart, isSameMonth(newStart, newEnd) ? addMonths(newStart, 0) : newEnd];
  } else {
    return [startDate, endDate];
  }
};

export const getLast7DaysRange = (date: Date): DateRange => ({
  startDate: startOfDay(addDays(date, -6)),
  endDate: endOfDay(date),
});

export const getLastDaysRange = (date: Date, daysBack: number): DateRange => ({
  startDate: startOfDay(addDays(date, -(daysBack - 1))),
  endDate: endOfDay(date),
});

export const getRangeType = (labelKey: PresetDateRanges): DateRangeType => {
  switch (labelKey) {
    case PresetDateRanges.LAST_MONTH:
      return DateRangeType.LAST_MONTH;
    case PresetDateRanges.YESTERDAY:
      return DateRangeType.YESTERDAY;
    default:
      return DateRangeType.DAYS_BACK;
  }
};

export const getDaysBack = (labelKey: PresetDateRanges, daysBack: number): number | undefined => {
  switch (labelKey) {
    case PresetDateRanges.LAST_MONTH:
    case PresetDateRanges.YESTERDAY:
      return undefined;

    default:
      return daysBack;
  }
};

export const getEndDate = (labelKey: PresetDateRanges, endDate: Date, timezone: string): Date | undefined => {
  switch (labelKey) {
    case PresetDateRanges.LAST_MONTH:
      return undefined;
    default:
      return toTimezoneDate(toLocalDate(endDate), timezone);
  }
};
